/**
 * The two limits on absence drawn as their rules read (owner, 22 Oct 2026 —
 * the Limits tab breaks the pair apart):
 *
 *   • a limit in total on its window's line (`InOutLimitLineDrawing`): the
 *     window's first and last day, today, every span of days the rule counted
 *     and the days nobody confirmed (hatched) — where the time away sits in,
 *     say, the permit's validity;
 *   • a limit in a row trip by trip (`InOutLimitTripsDrawing`): each run the
 *     rule measured against the limit, and — when the other limit holds one
 *     trip from today below it — how far the next one can go, dashed.
 *
 * Every day and position is the shared presentation's (`inoutLimitLine`,
 * `inoutLimitTrips`) from the engine's runs: nothing is counted here. For
 * assistive technology the same runs are a list. A closed InOut's drawings
 * (`pastDay`, H7) mark the day it was closed, not "today", and say its trip
 * under way and the next trip as of that day.
 */
import {
  inoutLimitLine,
  inoutLimitTrips,
  inoutLineMarkerText,
  inoutNextTripText,
  inoutOpenTripText,
  type InOutClock,
  type InOutEvaluation,
  type InOutPastDay,
} from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import { cn } from '@tonnie/ui';

import { INOUT_CLASSES } from '../lib/inout-tone';

/** Days nobody confirmed: hatched, never filled like days away (as on the timeline). */
const UNCERTAIN_LOOK =
  'bg-[repeating-linear-gradient(135deg,var(--color-warning)_0_2px,transparent_2px_5px)]';

/** A span drawn on a line is never thinner than a hairline a person can see. */
const width = (start: number, end: number) => `max(${end - start}%, 3px)`;

export function InOutLimitLineDrawing({
  clock,
  evaluation,
  pastDay = null,
}: {
  clock: InOutClock;
  evaluation: InOutEvaluation;
  /** The day a closed InOut is read as of, in words — null: today. */
  pastDay?: InOutPastDay;
}) {
  const { t, formatDate, formatNumber } = useLocale();
  const line = inoutLimitLine(clock, evaluation);
  if (!line) return null;
  const day = (date: string) => formatDate(date, 'calendarDay');

  return (
    <figure className="space-y-2" data-testid="inout-limit-line">
      <div className="relative pt-5">
        {line.today !== null && (
          <span
            className="absolute top-0 -translate-x-1/2 text-2xs font-semibold text-foreground"
            style={{ left: `${line.today}%` }}
            aria-hidden="true"
            data-testid="inout-limit-line-marker"
          >
            {inoutLineMarkerText(t, pastDay)}
          </span>
        )}
        <div className={cn('relative h-6 overflow-hidden rounded-md', INOUT_CLASSES.subtle)}>
          {line.runs.map((run) => (
            <span
              key={run.from}
              className={cn('absolute inset-y-0 rounded-sm', INOUT_CLASSES.solid)}
              style={{ left: `${run.start}%`, width: width(run.start, run.end) }}
              data-testid="inout-limit-line-run"
              aria-hidden="true"
            />
          ))}
          {line.uncertain.map((u) => (
            <span
              key={u.from}
              className={cn('absolute inset-y-0', UNCERTAIN_LOOK)}
              style={{ left: `${u.start}%`, width: width(u.start, u.end) }}
              data-testid="inout-limit-line-uncertain"
              aria-hidden="true"
            />
          ))}
        </div>
        {line.today !== null && (
          <span
            className="absolute bottom-0 top-4 w-0.5 -translate-x-1/2 bg-foreground"
            style={{ left: `${line.today}%` }}
            aria-hidden="true"
          />
        )}
      </div>
      <figcaption className="flex justify-between gap-3 text-xs text-muted-foreground tabular-nums">
        <span>{day(line.from)}</span>
        <span>{day(line.to)}</span>
      </figcaption>
      <ul
        className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
        aria-hidden="true"
      >
        <li className="flex items-center gap-1.5">
          <span className={cn('h-2 w-3.5 rounded-sm', INOUT_CLASSES.solid)} />
          {t('inout.limits.line.away')}
        </li>
        {line.uncertain.length > 0 && (
          <li className="flex items-center gap-1.5">
            <span className={cn('h-2 w-3.5 rounded-sm', UNCERTAIN_LOOK)} />
            {t('inout.limits.line.toConfirm')}
          </li>
        )}
      </ul>
      <ul className="sr-only">
        {line.runs.map((run) => (
          <li key={run.from}>
            {t('inout.limits.run', {
              count: run.days,
              from: day(run.from),
              to: day(run.to),
              days: formatNumber(run.days),
            })}
          </li>
        ))}
      </ul>
    </figure>
  );
}

export function InOutLimitTripsDrawing({
  clock,
  evaluation,
  pastDay = null,
}: {
  clock: InOutClock;
  evaluation: InOutEvaluation;
  /** The day a closed InOut is read as of, in words — null: today. */
  pastDay?: InOutPastDay;
}) {
  const { t, formatDate, formatNumber } = useLocale();
  const trips = inoutLimitTrips(clock, evaluation);
  if (!trips) return null;
  const day = (date: string) => formatDate(date, 'calendarDay');

  return (
    <div className="space-y-2" data-testid="inout-limit-trips">
      {trips.runs.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('inout.limits.trips.none')}</p>
      ) : (
        <ul className="space-y-1.5">
          {trips.runs.map((run) => (
            <li key={run.from} className="space-y-0.5" data-testid="inout-limit-trip">
              <span
                className={cn('block h-3 rounded-sm', INOUT_CLASSES.solid)}
                style={{ width: `max(${run.share * 100}%, 3px)` }}
                aria-hidden="true"
              />
              <span className="block text-xs text-foreground tabular-nums">
                {run.open
                  ? inoutOpenTripText(run, { t, formatNumber, from: day(run.from), pastDay })
                  : t('inout.limits.run', {
                      count: run.days,
                      from: day(run.from),
                      to: day(run.to),
                      days: formatNumber(run.days),
                    })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {trips.next && (
        <div className="space-y-0.5" data-testid="inout-limit-trips-next">
          <span
            className={cn('block h-3 rounded-sm border border-dashed', INOUT_CLASSES.edge)}
            style={{ width: `${trips.next.share * 100}%` }}
            aria-hidden="true"
          />
          <span className={cn('block text-xs font-medium', INOUT_CLASSES.text)}>
            {inoutNextTripText({ ...trips, next: trips.next }, { t, formatNumber, pastDay })}
          </span>
        </div>
      )}
      <p
        className="flex justify-between text-2xs text-muted-foreground tabular-nums"
        aria-hidden="true"
      >
        <span>{formatNumber(0)}</span>
        <span>{formatNumber(trips.limit)}</span>
      </p>
    </div>
  );
}
